import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Add, Edit, Delete, RoomService } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

const ServicePricingTab = ({ data, onRefresh, onSuccess, onError }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    serviceName: '',
    serviceType: 'Ziaraat',
    basePrice: 0,
    pricingType: 'PerPerson',
    description: '',
    isActive: true,
    displayOrder: 0,
  });

  const serviceTypes = ['Ziaraat', 'OnlineSupport', 'Insurance', 'VisaService', 'Transport', 'HotelService'];
  const pricingTypes = ['PerPerson', 'Fixed', 'PerDay'];

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        serviceName: item.serviceName,
        serviceType: item.serviceType,
        basePrice: item.basePrice,
        pricingType: item.pricingType,
        description: item.description || '',
        isActive: item.isActive,
        displayOrder: item.displayOrder || 0,
      });
    } else {
      setEditingItem(null);
      setFormData({
        serviceName: '',
        serviceType: 'Ziaraat',
        basePrice: 0,
        pricingType: 'PerPerson',
        description: '',
        isActive: true,
        displayOrder: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await axios.put(
          `${API_BASE_URL}/pricing/services/${editingItem.id}`,
          formData
        );
        onSuccess('Service pricing updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/pricing/services`, formData);
        onSuccess('Service pricing created successfully');
      }
      handleCloseDialog();
      onRefresh();
    } catch (err) {
      onError('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service pricing?')) {
      try {
        await axios.delete(`${API_BASE_URL}/pricing/services/${id}`);
        onSuccess('Service pricing deleted successfully');
        onRefresh();
      } catch (err) {
        onError('Failed to delete: ' + err.message);
      }
    }
  };

  const getPricingTypeLabel = (type) => {
    const labels = {
      'PerPerson': 'Per Person',
      'Fixed': 'Fixed',
      'PerDay': 'Per Day'
    };
    return labels[type] || type;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Additional Services Pricing</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Service Pricing
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Service Name</TableCell>
              <TableCell>Service Type</TableCell>
              <TableCell>Base Price</TableCell>
              <TableCell>Pricing Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RoomService fontSize="small" color="action" />
                    <Typography fontWeight="medium">{item.serviceName}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={item.serviceType} color="info" size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold" color="primary">
                    ${item.basePrice.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell>{getPricingTypeLabel(item.pricingType)}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </TableCell>
                <TableCell>{item.displayOrder}</TableCell>
                <TableCell>
                  <Chip
                    label={item.isActive ? 'Active' : 'Inactive'}
                    color={item.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(item)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Service Pricing' : 'Add Service Pricing'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Service Name"
              value={formData.serviceName}
              onChange={(e) =>
                setFormData({ ...formData, serviceName: e.target.value })
              }
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Service Type</InputLabel>
              <Select
                value={formData.serviceType}
                label="Service Type"
                onChange={(e) =>
                  setFormData({ ...formData, serviceType: e.target.value })
                }
              >
                {serviceTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Base Price ($)"
              type="number"
              value={formData.basePrice}
              onChange={(e) =>
                setFormData({ ...formData, basePrice: parseFloat(e.target.value) })
              }
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Pricing Type</InputLabel>
              <Select
                value={formData.pricingType}
                label="Pricing Type"
                onChange={(e) =>
                  setFormData({ ...formData, pricingType: e.target.value })
                }
              >
                {pricingTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {getPricingTypeLabel(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              label="Display Order"
              type="number"
              value={formData.displayOrder}
              onChange={(e) =>
                setFormData({ ...formData, displayOrder: parseInt(e.target.value) })
              }
              inputProps={{ min: 0 }}
              fullWidth
              helperText="Order in which service appears in listings"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServicePricingTab;
